import { Link, useRouterState } from '@tanstack/react-router';
import clsx from 'clsx';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  ACCESS_BLOCK_LABELS,
  ACCESS_BLOCK_ORDER,
  OPERATIONS,
  hasCapabilityGrant,
  type AccessBlock,
  type OperationId,
} from '@visualitics-toolkit/shared';

import { Button } from '../atoms';
import { AppVersionFooter, EnvBanner } from '../molecules';
import { ConnectionSwitcher } from '../organisms/ConnectionSwitcher';
import { useActiveConnection, useSession } from '../../lib/queries';
import { signOut } from '../../lib/auth';

type SidebarGroupKey = AccessBlock | 'sistema';

const SIDEBAR_STORAGE_KEY = 'toolkit.sidebar.access-blocks';

export function AppShell({ children }: { children: ReactNode }): ReactNode {
  const connection = useActiveConnection();
  const [switcherOpen, setSwitcherOpen] = useState(false);

  return (
    <div className="flex h-full flex-col" data-env={connection?.environment ?? 'local'}>
      <AppHeader connectionName={connection?.name} onSignOut={() => void signOut()} />
      <EnvBanner connection={connection} onSwitch={() => setSwitcherOpen(true)} />

      <div className="flex min-h-0 flex-1">
        <SidebarNav />
        <main className="min-w-0 flex-1 overflow-auto border-l border-neon/20 bg-surface/80 p-6">
          {children}
        </main>
      </div>

      <AppVersionFooter />

      {switcherOpen && <ConnectionSwitcher onClose={() => setSwitcherOpen(false)} />}
    </div>
  );
}

function AppHeader({
  connectionName,
  onSignOut,
}: {
  connectionName?: string;
  onSignOut: () => void;
}): ReactNode {
  return (
    <header className="flex items-center justify-between gap-4 border-b border-neon/25 bg-surface-raised/90 px-4 py-2.5 shadow-neon-sm backdrop-blur">
      <Link to="/" className="flex items-center gap-3 no-underline">
        <img
          src="/logo-visualitics.png"
          alt="Visualitics"
          className="h-8 w-auto drop-shadow-[0_0_10px_rgb(34_211_238_/_0.45)]"
        />
        <div className="leading-tight">
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-neon">Visualitics</p>
          <p className="text-sm font-semibold text-content">Toolkit</p>
        </div>
      </Link>

      <div className="flex items-center gap-2">
        {connectionName && (
          <span className="hidden font-mono text-[11px] text-muted sm:inline">{connectionName}</span>
        )}
        <Link
          to="/settings"
          className="rounded-md px-2 py-1 font-mono text-xs text-muted no-underline hover:text-neon"
        >
          Configurações
        </Link>
        <Button variant="ghost" className="font-mono text-xs" onClick={onSignOut}>
          Sair
        </Button>
      </div>
    </header>
  );
}

function SidebarNav(): ReactNode {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const session = useSession();
  const connection = useActiveConnection();
  const [collapsed, setCollapsed] = useState<Set<SidebarGroupKey>>(loadCollapsedGroups);

  const environment = connection?.environment ?? 'local';
  const grants = session.data?.grants;

  const operationsByBlock = useMemo(() => {
    return ACCESS_BLOCK_ORDER.map((block) => ({
      block,
      operations: (Object.keys(OPERATIONS) as OperationId[])
        .map((id) => OPERATIONS[id])
        .filter((operation) => {
          if (operation.accessBlock !== block || operation.route === null) return false;
          if (!grants) return false;
          return hasCapabilityGrant(grants, block, environment);
        }),
    })).filter((entry) => entry.operations.length > 0);
  }, [environment, grants]);

  const activeGroup = useMemo((): SidebarGroupKey | null => {
    if (pathname === '/') return null;
    if (
      pathname.startsWith('/sql-library') ||
      pathname.startsWith('/runs') ||
      pathname.startsWith('/db-insights') ||
      pathname.startsWith('/provider-collections') ||
      pathname.startsWith('/logs') ||
      pathname.startsWith('/settings')
    ) {
      return 'sistema';
    }
    const match = (Object.keys(OPERATIONS) as OperationId[])
      .map((id) => OPERATIONS[id])
      .find((operation) => operation.route === pathname);
    return match?.accessBlock ?? null;
  }, [pathname]);

  useEffect(() => {
    if (!activeGroup) return;
    setCollapsed((current) => {
      if (!current.has(activeGroup)) return current;
      const next = new Set(current);
      next.delete(activeGroup);
      persistCollapsedGroups(next);
      return next;
    });
  }, [activeGroup]);

  function toggle(group: SidebarGroupKey): void {
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      persistCollapsedGroups(next);
      return next;
    });
  }

  const sistemaOpen = !collapsed.has('sistema');
  const sistemaCount = session.data?.isAdmin ? 6 : 5;

  return (
    <nav aria-label="Navegação principal" className="w-64 shrink-0 overflow-auto bg-surface-raised/80 p-3">
      <NavItem to="/" label="Home" active={pathname === '/'} />

      {operationsByBlock.map(({ block, operations }) => {
        const open = !collapsed.has(block);
        const panelId = `nav-group-${block}`;
        return (
          <div key={block} className="mt-3">
            <GroupToggle
              label={ACCESS_BLOCK_LABELS[block]}
              count={operations.length}
              open={open}
              controls={panelId}
              onToggle={() => toggle(block)}
            />
            {open && (
              <div id={panelId}>
                {operations.map((operation) => (
                  <NavItem
                    key={operation.id}
                    to={operation.route as string}
                    label={operation.title}
                    active={pathname === operation.route}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}

      <div className="mt-3 border-t border-neon/15 pt-3">
        <GroupToggle
          label="Sistema"
          count={sistemaCount}
          open={sistemaOpen}
          controls="nav-group-sistema"
          onToggle={() => toggle('sistema')}
        />
        {sistemaOpen && (
          <div id="nav-group-sistema">
            <NavItem to="/sql-library" label="Biblioteca SQL" active={pathname.startsWith('/sql-library')} />
            <NavItem to="/runs" label="Histórico de runs" active={pathname.startsWith('/runs')} />
            <NavItem to="/db-insights" label="Insights do banco" active={pathname.startsWith('/db-insights')} />
            <NavItem
              to="/provider-collections"
              label="Coletas de providers"
              active={pathname.startsWith('/provider-collections')}
            />
            <NavItem to="/logs" label="Logs da aplicação" active={pathname.startsWith('/logs')} />
            <NavItem to="/settings" label="Configurações" active={pathname.startsWith('/settings')} />
          </div>
        )}
      </div>
    </nav>
  );
}

function GroupToggle({
  label,
  count,
  open,
  controls,
  onToggle,
}: {
  label: string;
  count: number;
  open: boolean;
  controls: string;
  onToggle: () => void;
}): ReactNode {
  return (
    <button
      type="button"
      aria-expanded={open}
      aria-controls={controls}
      onClick={onToggle}
      className="flex w-full items-center justify-between rounded-md px-3 py-1.5 text-left font-mono text-[11px] uppercase tracking-wider text-neon/80 hover:bg-surface hover:text-neon"
    >
      <span>
        {label} <span className="text-muted">({count})</span>
      </span>
      <span aria-hidden className="text-neon">
        {open ? '▾' : '▸'}
      </span>
    </button>
  );
}

function NavItem({ to, label, active }: { to: string; label: string; active: boolean }): ReactNode {
  return (
    <Link
      to={to}
      aria-current={active ? 'page' : undefined}
      className={clsx(
        'block truncate rounded-md px-3 py-1.5 text-sm transition',
        active
          ? 'bg-neon/10 font-medium text-neon shadow-neon-sm'
          : 'text-muted hover:bg-surface hover:text-content',
      )}
    >
      {label}
    </Link>
  );
}

function loadCollapsedGroups(): Set<SidebarGroupKey> {
  try {
    const raw = localStorage.getItem(SIDEBAR_STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((entry): entry is SidebarGroupKey => typeof entry === 'string'));
  } catch {
    return new Set();
  }
}

function persistCollapsedGroups(collapsed: Set<SidebarGroupKey>): void {
  localStorage.setItem(SIDEBAR_STORAGE_KEY, JSON.stringify([...collapsed]));
}
